/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import BaseModal from '../BaseModal/BaseModal';
import LoadingSpinner from '../../spinner/LoadingSpinner';
import { useGetFlexFacilitiesToFloatSlotQuery, useGetOnDemandFacilitiesToFloatSlotQuery } from '../../../redux/globalOverview';
import { user } from '../../../redux/user';
import { resolveSegmentUnitNames } from '../../../features/shifts/SplitShift/segmentUtils';
import FloatShiftDetails from './FloatShiftDetails';
import FloatActionStage from './FloatActionStage';
import FloatConfirmationStage from './FloatConfirmationStage';

import './FacilityFloatModal.scss';

const FLOAT_STAGES = {
	FLOAT_LOADING: 'FLOAT_LOADING',
	FLOAT_ACTION: 'FLOAT_ACTION',
	FLOAT_CONFIRMATION: 'FLOAT_CONFIRMATION',
	NO_FACILITIES_TO_FLOAT: 'NO_FACILITIES_TO_FLOAT'
};

const getFloatModalTitle = (slot) => slot.is_flex ? 'Flex facility float' : 'On Demand facility float';

const FacilityFloatModal = ({ onClose, onConfirm, shiftToFloat, isFeed }) => {
	const userData = useSelector(user, shallowEqual);
	const { shift, slot } = shiftToFloat;

	const [currentStep, setCurrentStep] = useState(FLOAT_STAGES.FLOAT_LOADING);
	const [floatError, setFloatError] = useState(null);
	const [selectedFacility, setSelectedFacility] = useState(null);
	const [selectedShiftStructure, setSelectedShiftStructure] = useState(null);
	const [selectedUnit, setSelectedUnit] = useState(null);
	const [nullOptionSelected, setNullOptionSelected] = useState(false);
	const [shiftStructureOptions, setShiftStructureOptions] = useState([]);
	const [unitsOptions, setUnitsOptions] = useState([]);
	const [bonusGuaranteedAgreed, setBonusGuaranteedAgreed] = useState(!slot.is_guaranteed && !slot.facility_payable_bonus);
	const isProcessingFloat = useRef(false);

	const flexQueryResult = useGetFlexFacilitiesToFloatSlotQuery({
		slotId: slot.slot_id,
		managementCompanyId: slot.management_company_id,
	}, {
		skip: !slot.is_flex,
		refetchOnMountOrArgChange: true,
	});
	const onDemandQueryResult = useGetOnDemandFacilitiesToFloatSlotQuery({
		slotId: slot.slot_id,
		managementCompanyId: slot.management_company_id,
	}, {
		skip: slot.is_flex,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: managementCompanyFacilities,
		isLoading,
		isError,
		isSuccess,
	} = slot.is_flex ? flexQueryResult : onDemandQueryResult;

	const managementCompanyFacilitiesOptions = useMemo(() => {
		if (!managementCompanyFacilities) {
			return [];
		}

		return managementCompanyFacilities.map((facility) => ({
			label: facility.label,
			value: facility.id,
			shiftStructures: facility.shiftStructures,
			units: facility.units
		}));
	}, [managementCompanyFacilities]);

	const shiftType = useMemo(() => {
		if (!shift) return '';

		if (isFeed) {
			return shift.resource_type;
		}

		return shift.shift_specialty
			? `${shift.shift_resource_type} - ${shift.shift_specialty}`
			: `${shift.shift_resource_type}`;
	}, [shift, isFeed]);

	useEffect(() => {
		const currentShiftStructures = [...(selectedFacility?.shiftStructures || [])]
			.sort((a, b) => {
				const aMatch = a.resource_type === shiftType;
				const bMatch = b.resource_type === shiftType;
				if (aMatch && !bMatch) return -1;
				if (!aMatch && bMatch) return 1;
				return a.resource_type.localeCompare(b.resource_type);
			})
			.map((ss) => ({
				label: (
					<>
						{ss.resource_type}
						{ss.resource_type === shiftType && <span className="current-unit-label">(current)</span>}
					</>
				),
				name: ss.resource_type,
				value: ss.shift_structure_id,
				is_matching: ss.resource_type === shiftType
			}));
		setShiftStructureOptions(currentShiftStructures);

		 if (slot.is_flex) {
			 const currentUnits = (selectedFacility?.units?.length ?? 0) > 0
				 ? [
					 { label: 'None', value: null },
					 ...selectedFacility.units.map((u) => ({
						 label: u.unit_name,
						 value: u.id
					 }))
				 ]
				 : []
			 setUnitsOptions(currentUnits);
		 }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFacility]);

	useEffect(() => {
		if (managementCompanyFacilities?.length === 1) {
			setSelectedFacility(managementCompanyFacilities[0]);
		}
	}, [managementCompanyFacilities]);

	useEffect(() => {
		if (shiftStructureOptions.length > 0 && shiftType) {
			const matchingStructure =
				shiftStructureOptions.length > 1
					? shiftStructureOptions.find((ss) => ss.is_matching)
					: shiftStructureOptions[0];
			if (matchingStructure) {
				setSelectedShiftStructure(matchingStructure.value);
			} else {
				setSelectedShiftStructure(null);
			}
		}
	}, [shiftType, shiftStructureOptions]);

	useEffect(() => {
		setSelectedUnit(null);
		if (unitsOptions.length === 1) {
			setSelectedUnit(unitsOptions[0].value);
			setNullOptionSelected(unitsOptions[0].value === null);
		}
	}, [unitsOptions]);

	useEffect(() => {
		if (!slot.is_flex) {
			const currentUnits = selectedFacility?.shiftStructures?.find(str => str.shift_structure_id === selectedShiftStructure)?.units.map((u) => ({
				label: u.unit_name,
				value: u.id
			})) || [];
			setUnitsOptions(currentUnits);
		}
	}, [selectedShiftStructure, selectedFacility]);

	useEffect(() => {
		if ((isSuccess && !managementCompanyFacilities?.length) || isError) {
			setCurrentStep(FLOAT_STAGES.NO_FACILITIES_TO_FLOAT);
		} else if (!isLoading) {
			setCurrentStep(FLOAT_STAGES.FLOAT_ACTION);
		}
	}, [managementCompanyFacilities, isError, isSuccess, isLoading]);

	const onSelectOption = ({ facility }) => {
		setSelectedFacility(
			managementCompanyFacilities.find((managementCompanyFacility) => managementCompanyFacility.id === facility)
		);
		setFloatError(false);
	};

	const onSelectStructure = ({ structure }) => {
		setSelectedShiftStructure(structure);
		setFloatError(false);
	};

	const onSelectUnit = ({ unit }) => {
		setSelectedUnit(unit);
		setFloatError(false);
		setNullOptionSelected(unit === null);
	};

	const handleOnConfirm = async () => {
		if (!isProcessingFloat.current) {
			isProcessingFloat.current = true;
			const res = await onConfirm(selectedFacility.id, slot.caregiver_id, selectedShiftStructure, selectedUnit);
			if (res && !!res?.data?.errors?.[0]) {
				setCurrentStep(FLOAT_STAGES.FLOAT_ACTION);
				setFloatError(res?.data?.errors?.[0]);
			}
			isProcessingFloat.current = false;
		}
	};

	const slotFacilityUnits = useMemo(() => {
		if (isFeed) {
			return userData.activeFacility.units;
		}
		const { facility_id: slotFacilityId } = shift || {};
		const slotFacility = (userData.facilitiesEnabled || []).find(
			(facility) => parseInt(facility.id, 10) === parseInt(slotFacilityId, 10)
		);
		return slotFacility?.units || [];
	}, [isFeed, userData.activeFacility.units, userData.facilitiesEnabled, shift]);

	const unitName = useMemo(() => {
		if (slot.has_segments) {
			return resolveSegmentUnitNames(slotFacilityUnits, slot.segment_unit_ids) || '-';
		}

		if (isFeed) {
			return userData.activeFacility.units.find((u) => u.id === slot.unit_id)?.unit_name || '-';
		}

		return slot.unit_name || '-';
	}, [slot, isFeed, userData.activeFacility.units, slotFacilityUnits]);

	const facilityChanges = useMemo(() => {
		if (currentStep !== FLOAT_STAGES.FLOAT_CONFIRMATION) return null;

		return {
			facility: selectedFacility,
			structure: shiftStructureOptions.find((ss) => ss.value === selectedShiftStructure),
			unit: unitsOptions?.find((u) => u.value === selectedUnit)?.label || 'None'
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, selectedFacility, selectedShiftStructure, selectedUnit]);

	const toggleBonusGuaranteedAgreed = () => setBonusGuaranteedAgreed(!bonusGuaranteedAgreed);

	const FLOAT_STAGES_DATA = {
		[FLOAT_STAGES.FLOAT_LOADING]: {
			modalTitle: getFloatModalTitle(slot),
			component: () => (
				<div>
					<LoadingSpinner />
					<p className="checking-facilities-text">Checking available facilities...</p>
				</div>
			),
			cancelAction: onClose,
			successBtnLabel: null,
			successBtnAction: null,
			modalClassName: 'facility-float-modal'
		},
		[FLOAT_STAGES.FLOAT_ACTION]: {
			modalTitle: getFloatModalTitle(slot),
			component: () => (
				<FloatActionStage
					managementCompanyFacilitiesOptions={managementCompanyFacilitiesOptions}
					shiftStructureOptions={shiftStructureOptions}
					unitsOptions={unitsOptions}
					selectedFacilityId={selectedFacility?.id || ''}
					selectedShiftStructure={selectedShiftStructure || ''}
					selectedUnit={selectedUnit}
					nullOptionSelected={nullOptionSelected}
					onSelectOption={onSelectOption}
					onSelectStructure={onSelectStructure}
					onSelectUnit={onSelectUnit}
					error={floatError}
				>
					<FloatShiftDetails isActionStage shift={shift} slot={slot} shiftType={shiftType} unitName={unitName} />
				</FloatActionStage>
			),
			cancelBtnLabel: 'Cancel',
			cancelAction: onClose,
			successBtnLabel: 'Change facility',
			successBtnAction: () => {
				setCurrentStep(FLOAT_STAGES.FLOAT_CONFIRMATION);
			},
			modalClassName: 'facility-float-modal'
		},
		[FLOAT_STAGES.FLOAT_CONFIRMATION]: {
			modalTitle: `Confirm ${getFloatModalTitle(slot)}`,
			component: () => (
				<FloatConfirmationStage
					isSplitShift={!!slot.has_segments}
					facilityChanges={facilityChanges}
					unitsOptions={unitsOptions}
					shiftType={shiftType}
					unitName={unitName}
					shift={shift}
					slot={slot}
					bonusGuaranteedAgreed={bonusGuaranteedAgreed}
					toggleBonusGuaranteedAgreed={toggleBonusGuaranteedAgreed}
				>
					<FloatShiftDetails shift={shift} slot={slot} shiftType={shiftType} unitName={unitName} />
				</FloatConfirmationStage>
			),
			cancelBtnLabel: 'Go back',
			cancelAction: () => {
				setCurrentStep(FLOAT_STAGES.FLOAT_ACTION);
				setBonusGuaranteedAgreed(!slot.is_guaranteed && !slot.facility_payable_bonus);
			},
			successBtnLabel: 'Confirm change',
			successBtnAction: handleOnConfirm,
			modalClassName: `facility-float-modal${slot.has_segments ? ' split-modal' : ''}`
		},
		[FLOAT_STAGES.NO_FACILITIES_TO_FLOAT]: {
			modalTitle: 'Facility float unavailable',
			component: () => (
				<div style={{ marginTop: '1rem' }}>
					<p>This professional cannot be floated to another facility for this shift.</p>
				</div>
			),
			successBtnLabel: 'Go back',
			successBtnAction: onClose,
			modalClassName: 'not-facility-float-modal',
			cancelAction: onClose
		}
	};

	return (
		<BaseModal
			title={FLOAT_STAGES_DATA[currentStep].modalTitle}
			className={FLOAT_STAGES_DATA[currentStep].modalClassName}
			primaryActionLabel={FLOAT_STAGES_DATA[currentStep].successBtnLabel}
			onPrimaryAction={FLOAT_STAGES_DATA[currentStep].successBtnAction}
			primaryActionDisabled={
				((!selectedFacility || !selectedShiftStructure) && currentStep !== FLOAT_STAGES.NO_FACILITIES_TO_FLOAT) ||
				(currentStep === FLOAT_STAGES.FLOAT_CONFIRMATION && (!bonusGuaranteedAgreed || isProcessingFloat.current))
			}
			secondaryActionLabel={FLOAT_STAGES_DATA[currentStep].cancelBtnLabel}
			onSecondaryAction={FLOAT_STAGES_DATA[currentStep].cancelAction}
			onHide={onClose}
			hideModalFooter={isLoading}
			showSecondaryBtn={currentStep !== FLOAT_STAGES.NO_FACILITIES_TO_FLOAT}
			confirmNewModal={currentStep === FLOAT_STAGES.NO_FACILITIES_TO_FLOAT}
			showCancel={false}
			confirmModalSize="l"
		>
			{FLOAT_STAGES_DATA[currentStep].component()}
		</BaseModal>
	);
};

export default FacilityFloatModal;
